import React from 'react';
import { useState } from 'react';
import {
  Bell,
  X,
  CheckCircle2,
  AlertCircle,
  Info,
  Award,
  CalendarClock,
  IndianRupee,
} from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';
export function NotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } =
    useNotifications();
  const getIcon = (type) => {
    switch (type) {
      case 'evaluation':
        return <Award className="w-5 h-5 text-green-600" />;
      case 'feedback':
        return <Info className="w-5 h-5 text-blue-600" />;
      case 'response':
        return <CheckCircle2 className="w-5 h-5 text-indigo-600" />;
      case 'due_reminder':
        return <CalendarClock className="w-5 h-5 text-rose-600" />;
      case 'fee':
        return <IndianRupee className="w-5 h-5 text-emerald-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-amber-600" />;
    }
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative rounded-xl p-2 text-slate-600 transition-all duration-200 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls="notification-center-panel"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-rose-500 text-white text-xs font-black rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <React.Fragment>
            <div
              className="fixed inset-0 z-40 bg-slate-900/20 sm:bg-transparent"
              onClick={() => setIsOpen(false)}
              aria-hidden
            />
            <motion.div
              id="notification-center-panel"
              role="region"
              aria-label="Notifications"
              initial={{
                opacity: 0,
                y: -10,
                scale: 0.98,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                y: -10,
                scale: 0.98,
              }}
              className={cn(
                'z-50 flex max-h-[min(36rem,80dvh)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl',
                /* Mobile/tablet: anchor to viewport so a wide panel does not shift left off-screen */
                'fixed left-3 right-3 top-[3.75rem] w-auto max-w-none sm:top-16',
                /* Desktop: dropdown under bell */
                'lg:absolute lg:inset-x-auto lg:right-0 lg:top-full lg:mt-2 lg:w-[min(24rem,calc(100vw-2rem))]'
              )}
            >
              <div className="lms-modal-header sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Bell className="h-5 w-5 shrink-0 text-slate-600" />
                  <h3 className="truncate font-black text-slate-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded">
                      {unreadCount}
                      {' new'}
                    </span>
                  )}
                </div>
                <div className="lms-modal-header__actions !flex-row !w-auto shrink-0 !items-center !gap-2 sm:!flex-row">
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={markAllAsRead}
                      className="!w-auto shrink-0 whitespace-nowrap px-2 text-xs font-bold text-indigo-600 hover:text-indigo-800"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="inline-flex !w-auto shrink-0 items-center justify-center p-2 text-slate-400 hover:text-slate-600"
                    aria-label="Close notifications"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto flex-1">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {notifications.map((notification) => (
                      <motion.div
                        key={notification.id}
                        initial={{
                          opacity: 0,
                          x: -20,
                        }}
                        animate={{
                          opacity: 1,
                          x: 0,
                        }}
                        className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${!notification.read ? 'bg-indigo-50/50' : ''}`}
                        onClick={() => {
                          if (!notification.read) {
                            markAsRead(notification.id);
                          }
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">{getIcon(notification.type)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h4 className="font-bold text-slate-900 text-sm">
                                {notification.title}
                              </h4>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0 mt-1.5" />
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{notification.message}</p>
                            <p className="text-xs text-slate-400">
                              {new Date(notification.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="flex-shrink-0 p-1 text-slate-400 hover:text-rose-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </React.Fragment>
        )}
      </AnimatePresence>
    </div>
  );
}
